import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BackendText from './BackendText';

/* ========= Clipboard helpers (no HTTPS dependency) ========= */
function safeCopyToClipboard(text) {
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    return navigator.clipboard.writeText(text).catch(() => legacyCopy(text));
  }
  return legacyCopy(text);
}

function legacyCopy(text) {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy'); // deprecated but widely supported
    document.body.removeChild(ta);
    return Promise.resolve();
  } catch {
    return Promise.reject(new Error('Copy failed'));
  }
}

/* ========= Optional helper to open UPI link ========= */
function openUpi(url) {
  // immediate navigation; on mobile this triggers app chooser
  window.location.href = url;
}

const Donate = () => {
  const { t } = useTranslation();

  // Bank details
  const bankDetails = {
    accountName: t('bank_account_name', 'Karigar Foundation'),
    accountNumber: '702120110000365',
    ifscCode: 'BKID0007021',
  };

  // ✅ Your UPI ID here
  const upiId = 'boim-702164000365@boi';

  // Optional prefill inputs (leave empty to keep flexible)
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  // Build a universal UPI deep link
  const upiLink = useMemo(() => {
    const params = new URLSearchParams({
      pa: upiId,
      pn: bankDetails.accountName || 'Karigar Foundation',
      cu: 'INR',
    });
    if (amount && !isNaN(Number(amount))) params.set('am', String(amount));
    if (note) params.set('tn', note);
    return `upi://pay?${params.toString()}`;
  }, [upiId, bankDetails.accountName, amount, note]);

  // Some stores like seeing the same upi:// scheme; kept here for future brand buttons
  const brandUpiLink = upiLink;

  const qrCodeUrl = './scanner.png';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
            <span className="inline-block pb-2 border-b-4 border-yellow-500">
              <BackendText i18nKey="donate" fallback="Support Our Cause" />
            </span>
          </h1>
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto">
            <BackendText
              i18nKey="donate_description"
              fallback="Your generous contribution helps us empower artisans and preserve traditional crafts across India."
            />
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-xl overflow-hidden mb-16">
          {/* Section Title */}
          <div className="bg-gray-900 text-white py-4 px-6">
            <h2 className="text-xl md:text-2xl font-semibold">
              <BackendText i18nKey="donation_methods" fallback="Ways to Donate" />
            </h2>
          </div>

          {/* Payment Methods */}
          <div className="md:grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
            {/* UPI QR + Deep Link */}
            <div className="p-8 md:p-10 flex flex-col items-center justify-center">
              <div className="bg-yellow-50 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                <BackendText i18nKey="pay_with_upi" fallback="Donate via UPI" />
              </h3>

              {/* Prefill inputs (optional) */}
              <div className="w-full max-w-sm grid grid-cols-1 gap-4 mb-6">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Amount (₹)</label>
                  <input
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputMode="decimal"
                    placeholder="e.g. 500"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Note (optional)</label>
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Donation / Your Name"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>
              </div>

              {/* Deep-link CTA */}
              <a
                href={upiLink}
                onClick={(e) => {
                  // prevent duplicate nav on some Android browsers
                  e.preventDefault();
                  openUpi(upiLink);
                }}
                className="w-full max-w-sm inline-flex items-center justify-center px-4 py-3 rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Open in any UPI app
              </a>

              {/* Optional brand buttons (use same universal link) */}
              <div className="flex gap-2 w-full max-w-sm mt-3">
                <a href={brandUpiLink} onClick={(e) => { e.preventDefault(); openUpi(brandUpiLink); }}
                   className="flex-1 text-center px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50">
                  Google&nbsp;Pay
                </a>
                <a href={brandUpiLink} onClick={(e) => { e.preventDefault(); openUpi(brandUpiLink); }}
                   className="flex-1 text-center px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50">
                  PhonePe
                </a>
                <a href={brandUpiLink} onClick={(e) => { e.preventDefault(); openUpi(brandUpiLink); }}
                   className="flex-1 text-center px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50">
                  Paytm
                </a>
              </div>

              {/* Show UPI ID + copy */}
              <div className="text-sm text-gray-700 mt-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium">UPI ID:</span>
                  <span className="font-mono">{upiId}</span>
                  <button
                    onClick={() => safeCopyToClipboard(upiId)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                    title="Copy UPI ID"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1 text-center">
                  Works best on mobile browsers. On desktop, use the QR below.
                </p>
              </div>

              {/* QR fallback */}
              <div className="bg-white p-2 border-2 border-gray-200 rounded-lg shadow-sm mt-6 mb-3">
                <img
                  src={qrCodeUrl}
                  alt="UPI QR Code"
                  className="w-56 h-56 md:w-64 md:h-64"
                />
              </div>
              <p className="text-center text-gray-600 max-w-xs">
                <BackendText
                  i18nKey="scan_qr_instructions"
                  fallback="Alternatively, scan this QR with any UPI app (Google Pay, PhonePe, Paytm)."
                />
              </p>
            </div>

            {/* Bank Transfer Details */}
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <div className="flex flex-col items-center md:items-start">
                <div className="bg-blue-50 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center md:text-left">
                  <BackendText i18nKey="bank_transfer" fallback="Direct Bank Transfer" />
                </h3>
              </div>

              <div className="space-y-6">
                <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex flex-col space-y-4">
                    <div>
                      <span className="text-sm text-gray-500 block mb-1">
                        <BackendText i18nKey="account_name" fallback="Account Name" />
                      </span>
                      <span className="text-lg font-medium text-gray-900">{bankDetails.accountName}</span>
                    </div>
                    <div className="pt-3 border-t border-gray-200">
                      <span className="text-sm text-gray-500 block mb-1">
                        <BackendText i18nKey="account_number" fallback="Account Number" />
                      </span>
                      <div className="flex items-center">
                        <span className="text-lg font-medium text-gray-900">{bankDetails.accountNumber}</span>
                        <button
                          onClick={() => safeCopyToClipboard(bankDetails.accountNumber)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                          title={t('copy_account_number', 'Copy account number')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="pt-3 border-t border-gray-200">
                      <span className="text-sm text-gray-500 block mb-1">
                        <BackendText i18nKey="ifsc_code" fallback="IFSC Code" />
                      </span>
                      <div className="flex items-center">
                        <span className="text-lg font-medium text-gray-900">{bankDetails.ifscCode}</span>
                        <button
                          onClick={() => safeCopyToClipboard(bankDetails.ifscCode)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                          title={t('copy_ifsc_code', 'Copy IFSC code')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <BackendText
                          i18nKey="bank_transfer_note"
                          fallback="Please include your name and contact information in the transfer details for acknowledgment."
                        />
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gray-900 text-white py-4 px-6">
            <h3 className="text-xl font-semibold">
              <BackendText i18nKey="need_help" fallback="Need Assistance?" />
            </h3>
          </div>
          <div className="p-6 md:p-8 flex flex-col md:flex-row items-center justify-between">
            <div className="text-center md:text-left mb-6 md:mb-0">
              <p className="text-gray-600 mb-2">
                <BackendText
                  i18nKey="donation_help_description"
                  fallback="If you have any questions about making a donation, our team is here to help."
                />
              </p>
              <p className="text-gray-900 font-medium">
                <BackendText
                  i18nKey="call_contact"
                  fallback="Contact us for support with your donation"
                />
              </p>
            </div>
            <a
              href={`callto:${t('contact_phone', '+918090846400')}`}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              
              <BackendText i18nKey="call_us" fallback="Call Us" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Donate;
